import { Injectable } from '@angular/core';
import { NgForage } from 'ngforage';
import { Observable, Subject } from 'rxjs';
import * as semaphore from 'semaphore';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import SemaphoreHelper from 'src/app/helpers/semaphore-helper';

@Injectable({
  providedIn: 'root'
})
export class ImmoIPPlacerSubmitQueueService {
  private readonly toSubmitStorageKey = 'immoToSubmit';
  private readonly toSubmitGetSemaphore = semaphore(1);
  private readonly toSubmitChange = new Subject<any>();

  constructor(private ngForage: NgForage) {}

  public async getToSubmit(): Promise<ImmoIPSubmitEntry[]> {
    let toReturn: ImmoIPSubmitEntry[] = null;
    await SemaphoreHelper.use(this.toSubmitGetSemaphore, async () => {
      toReturn = await this.ngForage.getItem(this.toSubmitStorageKey);
    });
    return toReturn;
  }

  public async submitTypeIsPresentForSubProject(
    subProjectId: number,
    submitType: ImmoIPSubmitType
  ): Promise<boolean> {
    let toReturn: boolean = false;
    await SemaphoreHelper.use(this.toSubmitGetSemaphore, async () => {
      const entries = await this.ngForage.getItem<ImmoIPSubmitEntry[]>(
        this.toSubmitStorageKey
      );
      toReturn =
        CollectionHelper.isPopulated(entries) &&
        entries.some(
          e =>
            e.subProjectId === subProjectId &&
            e.submitTypes.includes(submitType)
        );
    });
    return toReturn;
  }

  public async addToSubmit(
    dateString: string,
    subProjectId: number,
    submitType: ImmoIPSubmitType
  ): Promise<void> {
    await SemaphoreHelper.use(this.toSubmitGetSemaphore, async () => {
      let toSubmit: ImmoIPSubmitEntry[] = await this.ngForage.getItem(
        this.toSubmitStorageKey
      );
      toSubmit = toSubmit != null ? toSubmit : [];
      const earlierItem = toSubmit.find(e => e.subProjectId === subProjectId);
      if (earlierItem == null) {
        toSubmit.push({
          dateString: dateString,
          subProjectId: subProjectId,
          submitTypes: [submitType]
        });
      } else if (this.shouldAddToSubmitTypes(earlierItem, submitType)) {
        earlierItem.submitTypes.push(submitType);
        if (submitType === ImmoIPSubmitType.Full) {
          CollectionHelper.filterInPlace(
            earlierItem.submitTypes,
            t =>
              t !== ImmoIPSubmitType.RegisteredActions &&
              t !== ImmoIPSubmitType.ResetNotExecuted &&
              t !== ImmoIPSubmitType.SaveExecutionAssets &&
              t !== ImmoIPSubmitType.SaveNotExecutedAssets &&
              t !== ImmoIPSubmitType.ChangeInternalInfo
          );
        }
      }
      await this.ngForage.setItem(this.toSubmitStorageKey, toSubmit);
      this.toSubmitChange.next();
    });
  }

  private shouldAddToSubmitTypes(
    earlierItem: ImmoIPSubmitEntry,
    toWriteSubmitType: ImmoIPSubmitType
  ): boolean {
    if (toWriteSubmitType === ImmoIPSubmitType.Full) {
      return !earlierItem.submitTypes.includes(ImmoIPSubmitType.Full);
    }
    if (
      toWriteSubmitType === ImmoIPSubmitType.RegisteredActions ||
      toWriteSubmitType === ImmoIPSubmitType.ResetNotExecuted ||
      toWriteSubmitType === ImmoIPSubmitType.SaveExecutionAssets ||
      toWriteSubmitType === ImmoIPSubmitType.SaveNotExecutedAssets ||
      toWriteSubmitType === ImmoIPSubmitType.ChangeInternalInfo
    ) {
      return (
        !earlierItem.submitTypes.includes(ImmoIPSubmitType.Full) &&
        !earlierItem.submitTypes.includes(toWriteSubmitType)
      );
    }
    return !earlierItem.submitTypes.includes(toWriteSubmitType);
  }

  public async removeToSubmit(subProjectId: number): Promise<void> {
    if (subProjectId == null) {
      return;
    }
    await SemaphoreHelper.use(this.toSubmitGetSemaphore, async () => {
      const toSubmit: ImmoIPSubmitEntry[] = await this.ngForage.getItem(
        this.toSubmitStorageKey
      );
      if (!CollectionHelper.isPopulated(toSubmit)) {
        return;
      }
      CollectionHelper.filterInPlace(
        toSubmit,
        e => e.subProjectId !== subProjectId
      );
      await this.ngForage.setItem(this.toSubmitStorageKey, toSubmit);
    });
  }

  public async removeTypeFromSubmitEntry(
    subProjectId: number,
    toDeleteSubmitType: ImmoIPSubmitType
  ): Promise<void> {
    await SemaphoreHelper.use(this.toSubmitGetSemaphore, async () => {
      const toSubmit: ImmoIPSubmitEntry[] = await this.ngForage.getItem(
        this.toSubmitStorageKey
      );
      if (!CollectionHelper.isPopulated(toSubmit)) {
        return;
      }
      const matchingSubmitEntry = toSubmit.find(
        e => e.subProjectId === subProjectId
      );
      if (matchingSubmitEntry != null) {
        CollectionHelper.filterInPlace(
          matchingSubmitEntry.submitTypes,
          t => t !== toDeleteSubmitType
        );
        await this.ngForage.setItem(this.toSubmitStorageKey, toSubmit);
      }
    });
  }

  public getToSubmitChangeObservable(): Observable<any> {
    return this.toSubmitChange;
  }
}

export interface ImmoIPSubmitEntry {
  dateString: string;
  subProjectId: number;
  submitTypes: ImmoIPSubmitType[];
}

export enum ImmoIPSubmitType {
  Full,
  RegisteredActions,
  ResetNotExecuted,
  SaveExecutionAssets,
  SaveNotExecutedAssets,
  DeleteAssets,
  ChangeInternalInfo,
  PlacementConfig,
  Address
}
